import { useCallback, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Chip,
  Container,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import BuildIcon from "@mui/icons-material/Build";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import RestoreIcon from "@mui/icons-material/Restore";
import { format, parseISO } from "date-fns";
import {
  ExternalInvestmentBody,
  GetAdminInvestmentsParams,
  InvestmentApplication,
  InvestmentApplicationListItem,
  InvestmentApplicationStatus,
  InvestmentProject,
  InvestmentProjectListItem,
  InvestmentProjectStatus,
  INVESTMENT_APPLICATION_STATUS_LABELS,
  INVESTMENT_ARCHIVE_REASON_LABELS,
  INVESTMENT_PROJECT_STATUS_LABELS,
  useApproveInvestmentApplicationMutation,
  useCompleteInvestmentMutation,
  useCreateExternalInvestmentMutation,
  useGetAdminInvestmentApplicationsQuery,
  useGetAdminInvestmentsQuery,
  useHideInvestmentMutation,
  useLazyGetAdminInvestmentApplicationQuery,
  useLazyGetAdminInvestmentQuery,
  useNeedsRevisionInvestmentApplicationMutation,
  useRejectInvestmentApplicationMutation,
  useRemoveInvestmentMutation,
  useRestoreInvestmentMutation,
  useUnhideInvestmentMutation,
  useUpdateExternalInvestmentMutation,
} from "../../../entities/admin-investments";
import { useGetMyPermissionsQuery } from "../../../entities/me";
import { ADMIN_PERMISSION_INVESTMENTS } from "../../../shared/constants/admin-permission-ids";
import { LoadingIndicator } from "../../../shared/ui/loading-indicator";
import { InvestmentsApplicationViewDialog } from "./investments-application-view-dialog";
import { InvestmentsExternalFormDialog } from "./investments-external-form-dialog";
import { InvestmentsProjectViewDialog } from "./investments-project-view-dialog";
import {
  InvestmentReasonMode,
  InvestmentsReasonDialog,
} from "./investments-reason-dialog";

type InvestmentsTab = "moderation" | "registry";

const APPLICATION_STATUS_OPTIONS: {
  value: InvestmentApplicationStatus | "";
  label: string;
}[] = [
  { value: "", label: "Все статусы" },
  { value: "pendingReview", label: INVESTMENT_APPLICATION_STATUS_LABELS.pendingReview },
  { value: "needsRevision", label: INVESTMENT_APPLICATION_STATUS_LABELS.needsRevision },
  { value: "approved", label: INVESTMENT_APPLICATION_STATUS_LABELS.approved },
  { value: "rejected", label: INVESTMENT_APPLICATION_STATUS_LABELS.rejected },
  { value: "draft", label: INVESTMENT_APPLICATION_STATUS_LABELS.draft },
];

const PROJECT_STATUS_OPTIONS: {
  value: InvestmentProjectStatus | "";
  label: string;
}[] = [
  { value: "", label: "Все статусы" },
  { value: "active", label: INVESTMENT_PROJECT_STATUS_LABELS.active },
  { value: "archived", label: INVESTMENT_PROJECT_STATUS_LABELS.archived },
];

const formatListDate = (value?: string | null) => {
  if (!value) return "—";
  try {
    return format(parseISO(value), "dd.MM.yyyy");
  } catch {
    return value;
  }
};

const authorName = (item: { author?: { firstName?: string; lastName?: string } | null }) => {
  if (!item.author) return "—";
  return `${item.author.firstName ?? ""} ${item.author.lastName ?? ""}`.trim() || "—";
};

export const InvestmentsManagement = () => {
  const [activeTab, setActiveTab] = useState<InvestmentsTab>("moderation");
  const [search, setSearch] = useState("");
  const [applicationStatus, setApplicationStatus] = useState<
    InvestmentApplicationStatus | ""
  >("pendingReview");
  const [projectStatus, setProjectStatus] = useState<InvestmentProjectStatus | "">("");
  const [externalFilter, setExternalFilter] = useState<"" | "true" | "false">("");
  const [hiddenFilter, setHiddenFilter] = useState<"" | "true" | "false">("");
  const [archiveVisibility, setArchiveVisibility] = useState<"" | "true" | "false">("");
  const [sort, setSort] = useState<GetAdminInvestmentsParams["sort"]>("created");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [pageTokenHistory, setPageTokenHistory] = useState<(string | undefined)[]>([]);
  const [currentPageToken, setCurrentPageToken] = useState<string | undefined>();

  const [viewApplication, setViewApplication] = useState<InvestmentApplication | null>(null);
  const [openApplicationDialog, setOpenApplicationDialog] = useState(false);
  const [viewInvestment, setViewInvestment] = useState<InvestmentProject | null>(null);
  const [openInvestmentDialog, setOpenInvestmentDialog] = useState(false);
  const [openExternalForm, setOpenExternalForm] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState<InvestmentProject | null>(null);

  const [reasonDialogOpen, setReasonDialogOpen] = useState(false);
  const [reasonMode, setReasonMode] = useState<InvestmentReasonMode>("reject");
  const [reasonTargetId, setReasonTargetId] = useState<number | null>(null);
  const [reasonTargetTitle, setReasonTargetTitle] = useState("");

  const { data: permissionsData, isLoading: isLoadingPermissions } =
    useGetMyPermissionsQuery();
  const canAccess =
    permissionsData?.permissions?.includes(ADMIN_PERMISSION_INVESTMENTS) ?? false;

  const applicationsParams = useMemo(
    () => ({
      status: applicationStatus || undefined,
      pageToken: currentPageToken,
      limit,
    }),
    [applicationStatus, currentPageToken, limit],
  );

  const investmentsParams = useMemo(
    () => ({
      status: projectStatus || undefined,
      external:
        externalFilter === "" ? undefined : externalFilter === "true",
      isHidden: hiddenFilter === "" ? undefined : hiddenFilter === "true",
      visibleInPublicArchive:
        archiveVisibility === "" ? undefined : archiveVisibility === "true",
      search: search || undefined,
      sort,
      order,
      pageToken: currentPageToken,
      limit,
    }),
    [
      projectStatus,
      externalFilter,
      hiddenFilter,
      archiveVisibility,
      search,
      sort,
      order,
      currentPageToken,
      limit,
    ],
  );

  const {
    data: applicationsData,
    isLoading: isLoadingApplications,
    refetch: refetchApplications,
  } = useGetAdminInvestmentApplicationsQuery(applicationsParams, {
    skip: isLoadingPermissions || !canAccess || activeTab !== "moderation",
  });

  const {
    data: investmentsData,
    isLoading: isLoadingInvestments,
    refetch: refetchInvestments,
  } = useGetAdminInvestmentsQuery(investmentsParams, {
    skip: isLoadingPermissions || !canAccess || activeTab !== "registry",
  });

  const [fetchApplication, { isFetching: isFetchingApplication }] =
    useLazyGetAdminInvestmentApplicationQuery();
  const [fetchInvestment, { isFetching: isFetchingInvestment }] =
    useLazyGetAdminInvestmentQuery();

  const [approveApplication, { isLoading: isApproving }] =
    useApproveInvestmentApplicationMutation();
  const [rejectApplication, { isLoading: isRejecting }] =
    useRejectInvestmentApplicationMutation();
  const [needsRevisionApplication, { isLoading: isSendingRevision }] =
    useNeedsRevisionInvestmentApplicationMutation();
  const [createExternal, { isLoading: isCreating }] =
    useCreateExternalInvestmentMutation();
  const [updateExternal, { isLoading: isUpdating }] =
    useUpdateExternalInvestmentMutation();
  const [completeInvestment, { isLoading: isCompleting }] =
    useCompleteInvestmentMutation();
  const [removeInvestment, { isLoading: isRemoving }] =
    useRemoveInvestmentMutation();
  const [restoreInvestment, { isLoading: isRestoring }] =
    useRestoreInvestmentMutation();
  const [hideInvestment, { isLoading: isHiding }] = useHideInvestmentMutation();
  const [unhideInvestment, { isLoading: isUnhiding }] =
    useUnhideInvestmentMutation();

  const applications = applicationsData?.applications ?? [];
  const investments = investmentsData?.investments ?? [];
  const nextPageToken =
    (activeTab === "moderation"
      ? applicationsData?.pageToken
      : investmentsData?.pageToken
    )?.trim() || undefined;

  const resetPagination = useCallback(() => {
    setPage(0);
    setPageTokenHistory([]);
    setCurrentPageToken(undefined);
  }, []);

  const handleTabChange = (_: React.SyntheticEvent, value: InvestmentsTab) => {
    setActiveTab(value);
    resetPagination();
  };

  const handleChangePage = useCallback(
    (_: unknown, newPage: number) => {
      if (newPage > page) {
        if (nextPageToken) {
          setPageTokenHistory((prev) => [...prev, currentPageToken]);
          setCurrentPageToken(nextPageToken);
          setPage(newPage);
        }
      } else if (newPage < page) {
        const prev = [...pageTokenHistory];
        const lastPageToken = prev.pop();
        setPageTokenHistory(prev);
        setCurrentPageToken(lastPageToken);
        setPage(newPage);
      }
    },
    [page, nextPageToken, currentPageToken, pageTokenHistory],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setLimit(parseInt(event.target.value, 10));
      resetPagination();
    },
    [resetPagination],
  );

  const openApplicationDetail = async (id: number) => {
    setOpenApplicationDialog(true);
    setViewApplication(null);
    try {
      const result = await fetchApplication(id).unwrap();
      setViewApplication(result.application);
    } catch {
      alert("Не удалось загрузить заявку");
      setOpenApplicationDialog(false);
    }
  };

  const openInvestmentDetail = async (id: number) => {
    setOpenInvestmentDialog(true);
    setViewInvestment(null);
    try {
      const result = await fetchInvestment(id).unwrap();
      setViewInvestment(result.investment);
    } catch {
      alert("Не удалось загрузить проект");
      setOpenInvestmentDialog(false);
    }
  };

  const refreshApplicationIfOpen = async (id: number) => {
    if (viewApplication?.id !== id) return;
    try {
      const result = await fetchApplication(id).unwrap();
      setViewApplication(result.application);
    } catch {
      /* ignore */
    }
  };

  const refreshInvestmentIfOpen = async (id: number) => {
    if (viewInvestment?.id !== id) return;
    try {
      const result = await fetchInvestment(id).unwrap();
      setViewInvestment(result.investment);
    } catch {
      /* ignore */
    }
  };

  const handleApprove = async (id: number) => {
    if (!confirm("Одобрить заявку и опубликовать проект?")) return;
    try {
      await approveApplication(id).unwrap();
      refetchApplications();
      refetchInvestments();
      await refreshApplicationIfOpen(id);
    } catch {
      alert("Не удалось одобрить заявку");
    }
  };

  const openReasonDialog = (
    mode: InvestmentReasonMode,
    id: number,
    title: string,
  ) => {
    setReasonMode(mode);
    setReasonTargetId(id);
    setReasonTargetTitle(title);
    setReasonDialogOpen(true);
  };

  const handleReasonSubmit = async (rejectionReason: string) => {
    if (reasonTargetId == null) return;
    try {
      if (reasonMode === "reject") {
        await rejectApplication({
          id: reasonTargetId,
          body: { rejectionReason },
        }).unwrap();
      } else {
        await needsRevisionApplication({
          id: reasonTargetId,
          body: { rejectionReason },
        }).unwrap();
      }
      setReasonDialogOpen(false);
      setReasonTargetId(null);
      refetchApplications();
      await refreshApplicationIfOpen(reasonTargetId);
    } catch {
      alert(
        reasonMode === "reject"
          ? "Не удалось отклонить заявку"
          : "Не удалось отправить заявку на доработку",
      );
    }
  };

  const handleExternalSubmit = async (body: ExternalInvestmentBody) => {
    try {
      if (editingInvestment) {
        await updateExternal({ id: editingInvestment.id, body }).unwrap();
      } else {
        await createExternal(body).unwrap();
      }
      setOpenExternalForm(false);
      setEditingInvestment(null);
      refetchInvestments();
      if (editingInvestment) {
        await refreshInvestmentIfOpen(editingInvestment.id);
      }
    } catch {
      alert("Не удалось сохранить проект");
    }
  };

  const runInvestmentAction = async (
    action: () => Promise<unknown>,
    errorMessage: string,
    id: number,
  ) => {
    try {
      await action();
      refetchInvestments();
      await refreshInvestmentIfOpen(id);
    } catch {
      alert(errorMessage);
    }
  };

  if (isLoadingPermissions) {
    return <LoadingIndicator />;
  }

  if (!canAccess) {
    return (
      <Container sx={{ py: 4 }}>
        <Typography>Нет доступа к разделу Инвестиции</Typography>
      </Container>
    );
  }

  const isModerating = isApproving || isRejecting || isSendingRevision;
  const isActing =
    isCompleting || isRemoving || isRestoring || isHiding || isUnhiding;
  const isLoadingTable =
    activeTab === "moderation" ? isLoadingApplications : isLoadingInvestments;
  const rowsCount =
    activeTab === "moderation" ? applications.length : investments.length;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">Инвестиции</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingInvestment(null);
            setOpenExternalForm(true);
          }}
        >
          Внешний проект
        </Button>
      </Stack>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Модерация заявок" value="moderation" />
        <Tab label="Реестр проектов" value="registry" />
      </Tabs>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap">
          {activeTab === "moderation" ? (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Статус заявки</InputLabel>
              <Select
                value={applicationStatus}
                label="Статус заявки"
                onChange={(e) => {
                  setApplicationStatus(e.target.value as InvestmentApplicationStatus | "");
                  resetPagination();
                }}
              >
                {APPLICATION_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value || "all"} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <>
              <TextField
                size="small"
                placeholder="Поиск по названию или описанию"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  resetPagination();
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 260, flex: 1 }}
              />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Статус</InputLabel>
                <Select
                  value={projectStatus}
                  label="Статус"
                  onChange={(e) => {
                    setProjectStatus(e.target.value as InvestmentProjectStatus | "");
                    resetPagination();
                  }}
                >
                  {PROJECT_STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Тип</InputLabel>
                <Select
                  value={externalFilter}
                  label="Тип"
                  onChange={(e) => {
                    setExternalFilter(e.target.value as "" | "true" | "false");
                    resetPagination();
                  }}
                >
                  <MenuItem value="">Все</MenuItem>
                  <MenuItem value="false">Резидентские</MenuItem>
                  <MenuItem value="true">Внешние</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Скрытие</InputLabel>
                <Select
                  value={hiddenFilter}
                  label="Скрытие"
                  onChange={(e) => {
                    setHiddenFilter(e.target.value as "" | "true" | "false");
                    resetPagination();
                  }}
                >
                  <MenuItem value="">Все</MenuItem>
                  <MenuItem value="false">Видимые</MenuItem>
                  <MenuItem value="true">Скрытые админом</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Архив</InputLabel>
                <Select
                  value={archiveVisibility}
                  label="Архив"
                  onChange={(e) => {
                    setArchiveVisibility(e.target.value as "" | "true" | "false");
                    resetPagination();
                  }}
                >
                  <MenuItem value="">Все</MenuItem>
                  <MenuItem value="true">Публичный архив</MenuItem>
                  <MenuItem value="false">Скрытый архив</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Сортировка</InputLabel>
                <Select
                  value={sort}
                  label="Сортировка"
                  onChange={(e) => {
                    setSort(e.target.value as GetAdminInvestmentsParams["sort"]);
                    resetPagination();
                  }}
                >
                  <MenuItem value="created">По созданию</MenuItem>
                  <MenuItem value="published">По публикации</MenuItem>
                  <MenuItem value="title">По названию</MenuItem>
                  <MenuItem value="likes">По лайкам</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Порядок</InputLabel>
                <Select
                  value={order}
                  label="Порядок"
                  onChange={(e) => {
                    setOrder(e.target.value as "asc" | "desc");
                    resetPagination();
                  }}
                >
                  <MenuItem value="desc">Убыв.</MenuItem>
                  <MenuItem value="asc">Возр.</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        {isLoadingTable ? (
          <LoadingIndicator />
        ) : activeTab === "moderation" ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Проект</TableCell>
                <TableCell>Автор</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Создана</TableCell>
                <TableCell>Обновлена</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {applications.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Заявки не найдены
                  </TableCell>
                </TableRow>
              ) : (
                applications.map((item: InvestmentApplicationListItem) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar src={item.logoUrl ?? undefined} variant="rounded">
                          {item.title?.[0]}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500}>
                          {item.title || "Без названия"}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{authorName(item)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={INVESTMENT_APPLICATION_STATUS_LABELS[item.status]}
                      />
                    </TableCell>
                    <TableCell>
                      {item.targetInvestmentId
                        ? `Правка #${item.targetInvestmentId}`
                        : "Новая"}
                    </TableCell>
                    <TableCell>{formatListDate(item.createdAt)}</TableCell>
                    <TableCell>{formatListDate(item.updatedAt)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Открыть">
                        <IconButton
                          size="small"
                          onClick={() => void openApplicationDetail(item.id)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {item.status === "pendingReview" && (
                        <>
                          <Tooltip title="Одобрить">
                            <IconButton
                              size="small"
                              color="success"
                              disabled={isModerating}
                              onClick={() => void handleApprove(item.id)}
                            >
                              <ThumbUpIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="На доработку">
                            <IconButton
                              size="small"
                              color="warning"
                              disabled={isModerating}
                              onClick={() =>
                                openReasonDialog(
                                  "needsRevision",
                                  item.id,
                                  item.title,
                                )
                              }
                            >
                              <BuildIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Отклонить">
                            <IconButton
                              size="small"
                              color="error"
                              disabled={isModerating}
                              onClick={() =>
                                openReasonDialog("reject", item.id, item.title)
                              }
                            >
                              <ThumbDownIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Проект</TableCell>
                <TableCell>Автор</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Реакции</TableCell>
                <TableCell>Опубликован</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {investments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Проекты не найдены
                  </TableCell>
                </TableRow>
              ) : (
                investments.map((item: InvestmentProjectListItem) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar src={item.logoUrl ?? undefined} variant="rounded">
                          {item.title?.[0]}
                        </Avatar>
                        <Stack>
                          <Typography variant="body2" fontWeight={500}>
                            {item.title}
                          </Typography>
                          {item.isHidden && (
                            <Typography variant="caption" color="warning.main">
                              Скрыт админом
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell>{authorName(item)}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Chip
                          size="small"
                          label={INVESTMENT_PROJECT_STATUS_LABELS[item.status]}
                        />
                        {item.archiveReason && (
                          <Typography variant="caption" color="text.secondary">
                            {INVESTMENT_ARCHIVE_REASON_LABELS[item.archiveReason]}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={item.isExternal ? "Внешний" : "Резидентский"}
                        color={item.isExternal ? "primary" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      ▲ {item.likeCount} / ▼ {item.dislikeCount}
                    </TableCell>
                    <TableCell>{formatListDate(item.publishedAt)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Открыть">
                        <IconButton
                          size="small"
                          onClick={() => void openInvestmentDetail(item.id)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {item.isExternal && item.status === "active" && (
                        <Tooltip title="Редактировать">
                          <IconButton
                            size="small"
                            onClick={async () => {
                              try {
                                const result = await fetchInvestment(item.id).unwrap();
                                setEditingInvestment(result.investment);
                                setOpenExternalForm(true);
                              } catch {
                                alert("Не удалось загрузить проект");
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {item.status === "active" && (
                        <>
                          <Tooltip title="Исполнено">
                            <IconButton
                              size="small"
                              color="success"
                              disabled={isActing}
                              onClick={() => {
                                if (!confirm("Перевести проект в публичный архив?")) return;
                                void runInvestmentAction(
                                  () => completeInvestment(item.id).unwrap(),
                                  "Не удалось завершить проект",
                                  item.id,
                                );
                              }}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Снять с публикации">
                            <IconButton
                              size="small"
                              color="error"
                              disabled={isActing}
                              onClick={() => {
                                if (!confirm("Снять проект с публикации?")) return;
                                void runInvestmentAction(
                                  () => removeInvestment(item.id).unwrap(),
                                  "Не удалось снять проект",
                                  item.id,
                                );
                              }}
                            >
                              <UnpublishedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {item.status === "archived" && (
                        <Tooltip title="Восстановить">
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={isActing}
                            onClick={() => {
                              if (!confirm("Восстановить проект в Active?")) return;
                              void runInvestmentAction(
                                () => restoreInvestment(item.id).unwrap(),
                                "Не удалось восстановить проект",
                                item.id,
                              );
                            }}
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
        <TablePagination
          component="div"
          count={-1}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={limit}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[20, 50, 100]}
          labelDisplayedRows={({ page: currentPage }) =>
            `Страница ${currentPage + 1}${rowsCount ? ` · ${rowsCount} на стр.` : ""}`
          }
          nextIconButtonProps={{ disabled: !nextPageToken }}
          backIconButtonProps={{ disabled: page === 0 }}
        />
      </TableContainer>

      <InvestmentsApplicationViewDialog
        open={openApplicationDialog}
        application={viewApplication}
        isLoading={isFetchingApplication && !viewApplication}
        isModerating={isModerating}
        onClose={() => {
          setOpenApplicationDialog(false);
          setViewApplication(null);
        }}
        onApprove={() => viewApplication && void handleApprove(viewApplication.id)}
        onNeedsRevision={() =>
          viewApplication &&
          openReasonDialog(
            "needsRevision",
            viewApplication.id,
            viewApplication.title,
          )
        }
        onReject={() =>
          viewApplication &&
          openReasonDialog("reject", viewApplication.id, viewApplication.title)
        }
      />

      <InvestmentsProjectViewDialog
        open={openInvestmentDialog}
        investment={viewInvestment}
        isLoading={isFetchingInvestment && !viewInvestment}
        isActing={isActing}
        onClose={() => {
          setOpenInvestmentDialog(false);
          setViewInvestment(null);
        }}
        onEdit={() => {
          if (!viewInvestment) return;
          setEditingInvestment(viewInvestment);
          setOpenExternalForm(true);
        }}
        onComplete={() => {
          if (!viewInvestment) return;
          if (!confirm("Перевести проект в публичный архив?")) return;
          void runInvestmentAction(
            () => completeInvestment(viewInvestment.id).unwrap(),
            "Не удалось завершить проект",
            viewInvestment.id,
          );
        }}
        onRemove={() => {
          if (!viewInvestment) return;
          if (!confirm("Снять проект с публикации?")) return;
          void runInvestmentAction(
            () => removeInvestment(viewInvestment.id).unwrap(),
            "Не удалось снять проект",
            viewInvestment.id,
          );
        }}
        onRestore={() => {
          if (!viewInvestment) return;
          if (!confirm("Восстановить проект в Active?")) return;
          void runInvestmentAction(
            () => restoreInvestment(viewInvestment.id).unwrap(),
            "Не удалось восстановить проект",
            viewInvestment.id,
          );
        }}
        onHide={() => {
          if (!viewInvestment) return;
          void runInvestmentAction(
            () => hideInvestment(viewInvestment.id).unwrap(),
            "Не удалось скрыть проект",
            viewInvestment.id,
          );
        }}
        onUnhide={() => {
          if (!viewInvestment) return;
          void runInvestmentAction(
            () => unhideInvestment(viewInvestment.id).unwrap(),
            "Не удалось показать проект",
            viewInvestment.id,
          );
        }}
      />

      <InvestmentsReasonDialog
        open={reasonDialogOpen}
        mode={reasonMode}
        title={reasonTargetTitle}
        isSubmitting={isRejecting || isSendingRevision}
        onClose={() => setReasonDialogOpen(false)}
        onSubmit={(reason) => void handleReasonSubmit(reason)}
      />

      <InvestmentsExternalFormDialog
        open={openExternalForm}
        investment={editingInvestment}
        isSubmitting={isCreating || isUpdating}
        onClose={() => {
          setOpenExternalForm(false);
          setEditingInvestment(null);
        }}
        onSubmit={(body) => void handleExternalSubmit(body)}
      />
    </Container>
  );
};
